import type { DamnPdfData } from "./damnPdfDocument";
import { DAMN_PDF_MAP_LABELS } from "./damnPdfMaps";

export interface DamnPdfInclude {
    approachMap: boolean;
    approachGeotiff: boolean;
    siteMap: boolean;
    qrCode: boolean;
    instructions: boolean;
    haulRoute: boolean;
    routeSteps: boolean;
    otherRoutes: boolean;
    gate: boolean;
    gateToDropoffRoutes: boolean;
    dropoff: boolean;
    equipment: boolean;
    vehicle: boolean;
    ppe: boolean;
    generalContractor: boolean;
    receivingTeam: boolean;
    contacts: boolean;
}

export const DEFAULT_DAMN_PDF_INCLUDE: DamnPdfInclude = {
    approachMap: true,
    approachGeotiff: false,
    siteMap: true,
    qrCode: true,
    instructions: true,
    haulRoute: true,
    routeSteps: true,
    otherRoutes: true,
    gate: true,
    gateToDropoffRoutes: true,
    dropoff: true,
    equipment: true,
    vehicle: true,
    ppe: true,
    generalContractor: true,
    receivingTeam: true,
    contacts: true,
};

export interface DamnPdfOption {
    key: keyof DamnPdfInclude;
    label: string;
    // Only options this delivery has something for are offered.
    available: (data: DamnPdfData) => boolean;
}

const hasSteps = (data: DamnPdfData) =>
    (data.haulRoute?.steps.length ?? 0) > 0 || data.gateToDropoffRoutes.some((route) => route.steps.length > 0);

export const DAMN_PDF_OPTION_GROUPS: { title: string; options: DamnPdfOption[] }[] = [
    {
        title: "Overview",
        options: [
            {
                key: "approachMap",
                label: DAMN_PDF_MAP_LABELS.approach.option,
                available: (data) => data.maps.approach.available,
            },
            {
                key: "approachGeotiff",
                label: "GeoTIFF on haul route map",
                available: (data) => data.maps.approach.available && data.geotiff,
            },
            { key: "siteMap", label: DAMN_PDF_MAP_LABELS.site.option, available: (data) => data.maps.site.available },
            { key: "qrCode", label: "QR code to the live delivery page", available: (data) => !!data.qrCode },
            { key: "instructions", label: "Special instructions", available: (data) => !!data.instructions },
            { key: "ppe", label: "Required PPE", available: (data) => data.ppe.length > 0 },
        ],
    },
    {
        title: "Route to the dropoff",
        options: [
            // The assigned route when there is one, otherwise the approved routes to choose from.
            { key: "haulRoute", label: "Haul route", available: (data) => !!data.haulRoute },
            {
                key: "otherRoutes",
                label: "Approved haul routes",
                available: (data) => !data.haulRoute && data.alternateHaulRoutes.length > 0,
            },
            { key: "gate", label: "Gate", available: (data) => !!data.gate },
            {
                key: "gateToDropoffRoutes",
                label: "Gate-to-dropoff routes",
                available: (data) => data.gateToDropoffRoutes.length > 0,
            },
            { key: "routeSteps", label: "Turn-by-turn steps", available: hasSteps },
            { key: "dropoff", label: "Dropoff", available: () => true },
        ],
    },
    {
        title: "Delivery details",
        options: [
            { key: "equipment", label: "Unloading equipment", available: () => true },
            { key: "vehicle", label: "Vehicle", available: (data) => !!data.vehicle },
            { key: "generalContractor", label: "General contractor", available: () => true },
            { key: "receivingTeam", label: "Receiving team", available: () => true },
            // Absent for signed-out viewers, who can't see contacts on the page either.
            { key: "contacts", label: "Receiving team contacts", available: (data) => data.contacts !== null },
        ],
    },
];

// A per-browser convenience: the last choices are offered again next time. Storage can be unavailable (private
// windows, blocked site data), in which case the defaults are used.
const STORAGE_KEY = "damnPdfInclude";

export const loadDamnPdfInclude = (): DamnPdfInclude => {
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        return stored ? { ...DEFAULT_DAMN_PDF_INCLUDE, ...JSON.parse(stored) } : DEFAULT_DAMN_PDF_INCLUDE;
    } catch {
        return DEFAULT_DAMN_PDF_INCLUDE;
    }
};

export const saveDamnPdfInclude = (include: DamnPdfInclude) => {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(include));
    } catch {
        // Not remembering the choice is fine.
    }
};
